import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, take, fork, put, select, takeEvery } from 'redux-saga/effects';
import { EntityTypes } from 'constants/entities';
import { IAttemptAction } from 'types/action/player';
import { restoreState, updateState } from 'core/actions/product/state';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { RootState } from 'types';
import { selectActionFirst } from 'core/selectors/product/action/actionFirst';
import { selectModule } from 'core/selectors/product/module';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';
import {
  DialogueRegisterState,
  IActionRegister,
  IDialogueRegister,
  IPreview,
  IRegisterAction,
  RegisterResult,
  actionsComplete,
  next,
  preview,
  registerAction,
  registerActionResult,
  registerAttempt,
  registerDialogue,
  updateVerification,
} from 'core/slices/register';
import { startAction } from '../module/start';
import { CourseNodeRole, CourseNodeType, ICourseNodeDAO, ICourseNodeEntity } from 'types/domain/course';
import { selectCourseNode, selectDefaultCourseNode } from 'core/selectors/admin/courseNode';
import {
  processDialogue,
  processGroup,
  processModule,
  processNextNode,
  processNodeOut,
  processSystem,
  processVariable,
} from './processor';
import { PreviewType, clearPreview, updatePreview } from 'core/slices/preview';
import { ISelectNode } from 'core/slices/flow';
import { generateOfflineId } from 'core/slices/offline';
import { IVerificationEntity } from 'types/domain/verification';
import { createRequest, getRequest } from 'core/services/admin';
import { loadModule } from '../module/load';
import { moduleRequest } from 'core/services/product';
import { selectAction, selectActionDAO } from 'core/selectors/product/action/action';
import { selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { selectPreviewType } from 'core/selectors/home/preview';
import { IAction, IActionDAO } from 'types/domain/action';
import { Handles } from 'constants/handles';
import { queueItem } from 'core/slices/queue';

function* previewActionSaga(action: PayloadAction<IPreview>) {
  const offlineId = yield select(selectOfflineId);
  yield put(yield call(generateOfflineId, null));
  yield put(yield call(updateState, { type: EntityTypes.COURSE_PROGRESS, id: offlineId }));

  if (action.payload.moduleId) yield previewModule(action);
  else {
    const defaultNode = yield select(selectDefaultCourseNode);
    const courseNodeId = yield select((state: RootState) => state.state[EntityTypes.COURSE_NODE]);

    yield previewCourseNode(action.payload.courseNodeId || courseNodeId || defaultNode?.id);
  }
}

function* previewModule(action: PayloadAction<IPreview>) {
  const mode: Mode = yield select(selectSessionMode);
  const offlineId = yield select(selectOfflineId);
  const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);

  const actionId = yield select((state: RootState) => state.state[EntityTypes.ACTION]);
  let module = yield select((state: RootState) => selectModule(state, action.payload.moduleId));
  const selectedNode: ISelectNode = yield select((state: RootState) => state.flow.selectedNode);
  const selectedActionId = selectedNode && selectedNode.type === EntityTypes.ACTION ? selectedNode.id : null;

  let loading = yield loadModule(action.payload.moduleId);

  while (loading) {
    const modLoad = yield take([moduleRequest.fulfilled, getRequest.fulfilled]);
    loading = modLoad.payload.id !== action.payload.moduleId;
    module = yield select((state: RootState) => selectModule(state, action.payload.moduleId));
  }

  yield all([
    put(
      yield call(registerAttempt, {
        offlineId: offlineId,
        sessionId: offlineId,
        sessionAttemptId: offlineId,
        courseProgressId,
        moduleId: action.payload.moduleId,
        type: module.type,
        isPreview: true,
      } as IAttemptAction),
    ),
    put(
      yield call(restoreState, [
        {
          type: EntityTypes.SESSION_ATTEMPT,
          id: offlineId,
        },
      ]),
    ),
    put(
      yield call(updatePreview, {
        type: EntityTypes.MODULE,
        id: action.payload.moduleId,
        data: { courseProgressId },
      }),
    ),
  ]);

  yield put(
    yield call(queueItem, {
      courseNodeId: action.payload.courseNodeId,
      moduleId: action.payload.moduleId,
      courseProgressId,
      sessionAttemptId: offlineId,
    }),
  );

  //clear out any actionIds
  if (actionId && !action.payload.actionId && !selectedActionId)
    yield put(yield call(updateState, { type: EntityTypes.ACTION, id: null }));
  else if (action.payload.actionId) {
    let actionDAO = yield select((state: RootState) =>
      //@ts-ignore
      action.payload.actionId ? selectAction(state, action.payload.actionId) : null,
    );

    if (!actionDAO) return console.warn('no action');

    let saving = actionDAO.id === actionDAO.offlineId;

    while (saving) {
      const saved = yield take(createRequest.fulfilled);
      if (actionDAO.offlineId === saved.payload.offlineId) {
        saving = false;

        actionDAO = yield select((state: RootState) =>
          //@ts-ignore
          saved.payload.id ? selectAction(state, saved.payload.id) : null,
        );
      }
    }

    const moduleId = yield select(state => state.state[EntityTypes.MODULE]);

    if (actionDAO.moduleId === moduleId)
      yield put(yield call(updateState, { type: EntityTypes.ACTION, id: actionDAO.id, culprit: 7 }));
    // yield registerActionSaga(action.payload.actionId);
    // yield loadActionScene(action.payload.actionId);
    return;
  } else if (selectedActionId)
    return yield put(yield call(updateState, { type: EntityTypes.ACTION, id: selectedNode.id, culprit: 8 }));

  const firstAction = yield select(state => selectActionFirst(state, action.payload.moduleId));

  yield startAction(action.payload.actionId || firstAction?.id);
}

export function* previewCourseNode(courseNodeId: number, targetHandleId?: number, previousNodeId?: number) {
  let courseNode: ICourseNodeDAO = yield select((state: RootState) => selectCourseNode(state, courseNodeId));

  while (!courseNode) {
    yield take(getRequest.fulfilled);
    courseNode = yield select((state: RootState) => selectCourseNode(state, courseNodeId));
  }

  if (!courseNode || courseNode?.status === -1) return;

  yield put(
    yield call(updateState, { type: EntityTypes.COURSE_NODE, id: courseNode.id, culprit: 'previewCourseNode' }),
  );

  switch (courseNode.type) {
    case CourseNodeType.START:
      return yield processSystem(courseNode);
    case CourseNodeType.ASSIGN:
      switch (courseNode.entityType) {
        case CourseNodeRole.VARIABLE_SINGLE:
        case CourseNodeRole.VARIABLE:
          return yield processVariable(courseNode, targetHandleId, previousNodeId);
        case CourseNodeRole.MODULE:
          return yield processModule(courseNode);
      }
      break;
    case CourseNodeType.END:
    case CourseNodeType.RESPONSE:
    case CourseNodeType.REQUEST:
    case CourseNodeType.NOTIFICATION:
      return yield processDialogue(courseNode);
    case CourseNodeType.MENU: {
      //yield put(yield call(clearHistory));
      return yield processDialogue(courseNode);
    }
    case CourseNodeType.LOGIC:
      switch (courseNode.entityType) {
        case CourseNodeRole.VARIABLE_SINGLE:
        case CourseNodeRole.VARIABLE:
          return yield processVariable(courseNode, targetHandleId, previousNodeId);
        case CourseNodeRole.GROUP:
          return yield processGroup(courseNode, targetHandleId);
      }
  }
}

function* updateVerificationSaga(action: PayloadAction<any>) {
  const mode: Mode = yield select(selectSessionMode);
  const verificationEntities = yield select(
    (state: RootState) => state.entity.present[EntityTypes.VERIFICATION_ENTITY],
  );
  if (mode === Mode.PREVIEW && action.payload.entities) {
    yield all(
      Object.values(action.payload.entities).map((entity: any) => {
        const verifEntity: IVerificationEntity = verificationEntities[entity.verificationEntityId];
        return processNodeOut(verifEntity.courseNodeId, verifEntity.id);
      }),
    );
    yield put(yield call(next, {}));
  }
}

function* actionsCompleteSaga(action: PayloadAction<IRegisterAction>) {
  const feed = yield select(selectActionFeed);
  const mode: Mode = yield select(selectSessionMode);
  const previewType: PreviewType = yield select(selectPreviewType);

  if (action.payload && action.payload.actionId) {
    //@ts-ignore
    const actionDAO: IActionDAO = yield select((state: RootState) => selectActionDAO(state, action.payload.actionId));

    if (action.payload.actionEntityId) {
      const modOut = actionDAO.outs[action.payload.actionEntityId];
      if (modOut) return;
    }

    switch (action.payload.result) {
      case RegisterResult.DEFAULT:
        if (actionDAO.outs[Handles.DEFAULT]) return;
        break;
    }
  }

  if (
    previewType === PreviewType.COURSE &&
    mode === Mode.PREVIEW &&
    [feed.feed.length, feed.feed.length - 1].includes(feed.index)
  ) {
    const courseNode: ICourseNodeDAO = yield select(selectCourseNode);

    if (!courseNode) return;

    if (courseNode.variableId || courseNode.moduleId) {
      const success = yield processVariable(courseNode, null, null);
      if (!success) yield processNextNode();
    } else yield processNextNode();
  }
}

function* registerDialogueSaga(action: PayloadAction<IDialogueRegister>) {
  const mode: Mode = yield select(selectSessionMode);

  if (mode !== Mode.PREVIEW) return;

  const courseNode: ICourseNodeDAO = yield select((state: RootState) =>
    selectCourseNode(state, action.payload.courseNodeId),
  );

  if (courseNode.type === CourseNodeType.MENU) {
    yield previewCourseNode(action.payload.targetCourseNodeId);
    yield put(yield call(next, {}));
    return;
  }

  if (courseNode.entityType === CourseNodeRole.GROUP && action.payload.courseNodeEntityId) {
    const entity: ICourseNodeEntity = courseNode.entities.find(ent => ent.id === action.payload.courseNodeEntityId);

    if (entity.groupId) yield put(yield call(updatePreview, { type: EntityTypes.GROUP, id: entity.groupId }));

    if (entity && entity.outs.length)
      yield processNodeOut(action.payload.courseNodeId, action.payload.courseNodeEntityId);
  }

  if (action.payload.state === DialogueRegisterState.SUBMITTED) {
    yield processNextNode(courseNode.id);
    yield put(yield call(next, {}));
  }
}

function* clearPreviewSaga() {
  const previewType: PreviewType = yield select((state: RootState) => state.preview?.type);

  yield put(
    yield call(restoreState, [
      { type: EntityTypes.COURSE_PROGRESS, id: null },
      { type: EntityTypes.COURSE_NODE, id: null },
      { type: EntityTypes.SESSION_ATTEMPT, id: null },
      { type: EntityTypes.ACTION, id: null },
      { type: EntityTypes.SCENE, id: null },
      { type: EntityTypes.VERIFICATION_REQUEST, id: null },
      { ...(previewType === PreviewType.COURSE ? { type: EntityTypes.MODULE, id: null } : null) },
    ]),
  );
}
function* registerActionSaga(action: PayloadAction<IActionRegister>) {
  const a: IAction = yield select(
    (state: RootState) => state.entity.present[EntityTypes.ACTION][action.payload.actionId],
  );
  const variableId = yield select(
    (state: RootState) => state.entity.present[EntityTypes.MODULE][a.moduleId]?.variableId,
  );

  const previewVariable = yield select((state: RootState) => state.preview?.variables[variableId]);

  if (!previewVariable)
    yield put(
      yield call(updatePreview, {
        type: EntityTypes.VARIABLE,
        id: variableId,
        data: {},
      }),
    );
}

function* watchPreview() {
  yield takeEvery(preview, previewActionSaga);
}

function* watchActionsComplete() {
  yield takeEvery([registerActionResult, actionsComplete], actionsCompleteSaga);
}

function* watchUpdateVerification() {
  yield takeEvery(updateVerification, updateVerificationSaga);
}

function* watchRegisterAction() {
  yield takeEvery(registerAction, registerActionSaga);
}

function* watchRegisterDialogue() {
  yield takeEvery(registerDialogue, registerDialogueSaga);
}

function* watchClearPreview() {
  yield takeEvery(clearPreview, clearPreviewSaga);
}

export function* previewSaga() {
  yield all([
    fork(watchPreview),
    fork(watchActionsComplete),
    fork(watchUpdateVerification),
    fork(watchRegisterDialogue),
    fork(watchRegisterAction),
    fork(watchClearPreview),
  ]);
}
